import React from 'react';
import type { VelaSuggestion } from '../types';
import { COA_COL_WIDTHS } from '../constants';
import Toolbar from './Toolbar';
import FilterBar from './FilterBar';

const COL_LABELS: Record<string, string> = {
  name: 'Name',
  type: 'Account type',
  detailType: 'Detail type',
  novalaBalance: 'Novala balance',
  bankBalance: 'Bank balance',
};

const COL_ALIGN: Record<string, 'left' | 'right'> = {
  name: 'left',
  type: 'left',
  detailType: 'left',
  novalaBalance: 'right',
  bankBalance: 'right',
};

interface VisibleCol {
  key: string;
}

interface Props {
  suggestionsCount: number;
  onVelaClick: () => void;
  onNewAccount: () => void;
  onRunReport: () => void;
  onImportChartOfAccounts: () => void;
  onCreateMultiple: () => void;
  onStartVelaCleanup: () => void;
  onArchiveUnused: () => void;
  onMergeDuplicates: () => void;
  onCategorizeUncategorized: () => void;
  onOpenEditMode: () => void;
  onViewCleanupHistory: () => void;
  query: string;
  onQueryChange: (q: string) => void;
  typeFilter: string;
  onTypeFilterChange: (t: string) => void;
  pageStart: number;
  pageEnd: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onExport: () => void;
  onPrint: () => void;
  onCustomize: () => void;
  suggestions: VelaSuggestion[];
  visibleCols: VisibleCol[];
  sortBy: 'name' | 'type';
  sortDir: 'asc' | 'desc';
  onSort: (col: 'name' | 'type') => void;
  allPagedSelected: boolean;
  somePagedSelected: boolean;
  onToggleAllPaged: () => void;
  headerCheckboxRef: React.RefObject<HTMLInputElement | null>;
}

export default function CoaStickyHeader(props: Props): React.ReactElement {
  const colW = (key: string): React.CSSProperties => {
    const w = COA_COL_WIDTHS[key];
    return w == null ? {} : { width: w };
  };

  return (
    <div className="sticky -top-7 z-20 bg-white -mx-4 md:-mx-8 px-4 md:px-8 pt-8 pb-0 no-print">
      <div className="flex justify-end mb-3">
        <Toolbar
          velaCount={props.suggestionsCount}
          onVelaClick={props.onVelaClick}
          onNewAccount={props.onNewAccount}
          onRunReport={props.onRunReport}
          onImportChartOfAccounts={props.onImportChartOfAccounts}
          onCreateMultiple={props.onCreateMultiple}
          onStartVelaCleanup={props.onStartVelaCleanup}
          onArchiveUnused={props.onArchiveUnused}
          onMergeDuplicates={props.onMergeDuplicates}
          onCategorizeUncategorized={props.onCategorizeUncategorized}
          onOpenEditMode={props.onOpenEditMode}
          onViewCleanupHistory={props.onViewCleanupHistory}
        />
      </div>

      <FilterBar
        query={props.query}
        onQueryChange={props.onQueryChange}
        typeFilter={props.typeFilter}
        onTypeFilterChange={props.onTypeFilterChange}
        pageStart={props.pageStart}
        pageEnd={props.pageEnd}
        total={props.total}
        onPrev={props.onPrev}
        onNext={props.onNext}
        onExport={props.onExport}
        onPrint={props.onPrint}
        onCustomize={props.onCustomize}
      />

      {props.suggestions.length > 0 && (
        <button
          type="button"
          onClick={props.onVelaClick}
          className="inline-flex items-center gap-2 bg-vela-fade border border-vela-wash rounded-full pl-1.5 pr-2 py-1 text-left cursor-pointer hover:brightness-95 mt-3"
        >
          <span className="w-5 h-5 rounded-full bg-gradient-to-br from-vela to-vela-deep text-white text-[10px] font-extrabold inline-flex items-center justify-center flex-shrink-0">V</span>
          <span className="text-tiny text-vela-deep font-semibold whitespace-nowrap">
            <strong className="font-bold">{props.suggestions.length} for you</strong>
            {' · '}
            {props.suggestions.map((s) => s.typeLabel.toLowerCase()).join(', ')}
          </span>
          <span className="bg-vela-deep text-white rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold ml-1">Review</span>
        </button>
      )}

      <div className="hidden md:block mt-3 bg-tableHeader border-y border-line">
        <table className="w-full border-collapse table-fixed">
          <colgroup>
            <col style={colW('checkbox')} />
            {props.visibleCols.map((v) => (
              <col key={v.key} style={colW(v.key)} />
            ))}
            <col style={colW('action')} />
          </colgroup>
          <thead>
            <tr>
              <th className="py-2.5 px-3.5">
                <input
                  ref={props.headerCheckboxRef}
                  type="checkbox"
                  checked={props.allPagedSelected}
                  onChange={props.onToggleAllPaged}
                  className="w-4 h-4 cursor-pointer accent-teal-dark"
                  aria-label="Select all accounts on this page"
                />
              </th>
              {props.visibleCols.map((v) => {
                const col = v.key;
                const align = COL_ALIGN[col] ?? 'left';
                const isSortable = col === 'name' || col === 'type';
                const active = isSortable && props.sortBy === col;
                return (
                  <th
                    key={col}
                    onClick={isSortable ? () => props.onSort(col as 'name' | 'type') : undefined}
                    className={`py-2.5 px-3.5 text-tiny font-bold text-ink2 uppercase tracking-wider whitespace-nowrap ${
                      align === 'right' ? 'text-right' : 'text-left'
                    } ${isSortable ? 'cursor-pointer select-none' : ''}`}
                  >
                    {COL_LABELS[col]}
                    {isSortable && (
                      <span className={`ml-1 text-[10px] ${active ? 'text-ink2' : 'text-ink4'}`}>
                        {active ? (props.sortDir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    )}
                  </th>
                );
              })}
              <th className="py-2.5 px-3.5 text-right text-tiny font-bold text-ink2 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
        </table>
      </div>
    </div>
  );
}